<template>
  <v-row v-loading="!dataLoaded">

    <v-col cols="12" class="pt-0">
      <PageTitle title="Учебные периоды" />
    </v-col>

    <v-col cols="12" class="pt-0">
      <v-btn
        small
        outlined
        rounded
        color="primary"
        class="white--text px-4"
        @click="dialog = true"
      >
        <v-icon left small>mdi-plus</v-icon>Добавить
      </v-btn>
    </v-col>

    <v-col cols="12" class="py-0">
      <PageTable
        :table-loading="tableLoading"
        :table-headers="tableHeaders"
        :table-items="localSubmissionSessionParent.list"
        @get-data="loadMore"
      >
        <template v-slot:top>
          <div class="d-flex align-center justify-end pa-2">
            <v-badge overlap dot :value="hasActiveFilter">
              <v-btn
                small
                rounded
                outlined
                color="primary"
                class="white--text filter-btn"
                @click="showFilter = !showFilter"
              >
                <v-icon left small>mdi-filter-outline</v-icon>
                <span>{{ showFilter ? 'Закрыть' : 'Фильтр' }}</span>
              </v-btn>
            </v-badge>
          </div>
        </template>

        <template v-slot:item.semester="{ item }">
          {{ getSemesterName(item.semester) }}
        </template>

        <template v-slot:item.year="{ item }">
          {{ item.year?.name || '-' }}
        </template>

        <template v-slot:item.isActual="{ item }">
          <v-chip
            :color="item.isActual ? 'success' : 'grey'"
            small
            dark
          >
            {{ item.isActual ? 'Активен' : 'Неактивен' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon
            color="lightGrey"
            class="mr-1 edit-btn"
            size="22"
            title="Редактировать"
            @click="edit(item)"
          >
            mdi-pencil-outline
          </v-icon>
          <v-icon
            color="lightGrey"
            class="remove-btn"
            size="22"
            title="Удалить"
            @click="remove(item)"
          >
            mdi-trash-can-outline
          </v-icon>
        </template>

        <template v-slot:footer>
          <div ref="scrollTrigger" class="infinite-scroll-trigger py-4 text-center">
            <v-progress-circular
              v-if="loadingMore"
              indeterminate
              color="primary"
              size="30"
              width="3"
            />
          </div>
        </template>
      </PageTable>
    </v-col>

    <SubmissionSessionDialog
      :dialog="dialog"
      :loading="btnLoading"
      :selectedItem="selectedItem"
      @saveSubmissionSession="saveSubmissionSession"
      @closeModal="closeDialog"
    />

    <ConfirmDialog
      :dialog="confirmDialog"
      :loading="btnLoading"
      :message="`Вы уверены, что хотите удалить учебный период?`"
      @confirm="removeSubmissionSession"
      @closeModal="closeDialog"
    />

    <transition name="filter">
      <SubmissionSessionFilter
        v-show="showFilter"
        :filter="submissionFilter"
        @applyFilter="applyFilter"
        @closeFilter="showFilter = false"
        :width-filter="300"
        :top="229"
        :right="29"
      />
    </transition>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'
import PageTitle from '@/components/ui/Title'
import PageTable from '@/components/ui/Table'
import { SubmissionSessionParent, SubmissionSession } from '@/models'
import { submissionSessionApi } from '@/api'
import SubmissionSessionDialog from '@/components/zvp/submissionSession/SubmissionSessionDialog'
import ConfirmDialog from '@/components/dialogs/ConfirmDelete'
import SubmissionSessionFilter from "@/components/zvp/submissionSession/SubmissionSessionFilterDialog"

export default {
  name: "SubmissionSession",

  components: {
    PageTitle,
    PageTable,
    ConfirmDialog,
    SubmissionSessionDialog,
    SubmissionSessionFilter
  },

  async created() {
    if (!this.checkRoles) {
      await this.$router.push('/')
    } else {
      await this.loadInitialData()
      this.dataLoaded = true
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },

  data() {
    return {
      tableHeaders: [
        { text: 'Семестр', value: 'semester', sortable: false, align: 'start' },
        { text: 'Год', value: 'year', sortable: false, align: 'start' },
        { text: 'Статус', value: 'isActual', sortable: false, align: 'center', width: '120px' },
        { text: 'Действия', value: 'actions', sortable: false, align: 'center', width: '130px' }
      ],
      localSubmissionSessionParent: new SubmissionSessionParent(),
      tableLoading: false,
      selectedItem: null,
      confirmDialog: false,
      btnLoading: false,
      dataLoaded: false,
      dialog: false,
      page: 0,
      pageSize: 10,
      totalPages: 0,
      loadingMore: false,
      hasMore: true,
      observer: null,
      showFilter: false,
      submissionFilter: {},
      isLoading: false,
      scrollTimeout: null, 
    }
  },

  computed: {
    ...mapGetters('user', ['isCurator', 'isAdmin']),

    checkRoles() {
      return this.isCurator || this.isAdmin
    },

    hasActiveFilter() {
      return !!this.submissionFilter.year?.id
    }
  },

  methods: {
    getSemesterName(semester) {
      if (!semester) return '-'
      const names = {
        1: 'Осенний',
        2: 'Весенний'
      }
      return names[semester.id] || semester.name || '-'
    },

    async applyFilter(filter) {
      this.submissionFilter = filter || {}
      this.page = 0
      this.hasMore = true
      this.localSubmissionSessionParent.list = []
      this.showFilter = false
      await this.loadInitialData()
    },

    async loadInitialData() {
      if (this.isLoading) return 

      this.isLoading = true
      this.tableLoading = true
      try {
        this.page = 0 
        
        const response = await submissionSessionApi.getCatalogPagination(
          this.page,                          
          this.pageSize,                       
          this.submissionFilter.year?.name       
        )
        
        const data = response.content || []
        this.totalPages = response.totalPages || 0
        this.hasMore = this.page < this.totalPages - 1
        
        this.localSubmissionSessionParent.list = data || []
      } catch(e) {
        this.showErrorMessage(e)
      } finally {
        this.tableLoading = false
        this.isLoading = false
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore || this.isLoading) return
      
      this.loadingMore = true
      const nextPage = this.page + 1 
      
      try {
        const response = await submissionSessionApi.getCatalogPagination(
          nextPage,                           
          this.pageSize,                      
          this.submissionFilter.year?.name      
        )
        const data = response.content || []
        
        this.hasMore = nextPage < this.totalPages - 1
        
        if (data?.length) {
          const newItems = data.map(el => SubmissionSession.buildFromAPI(el))
          this.localSubmissionSessionParent.list.push(...newItems)
          this.page = nextPage
        }
      } catch(e) {
        this.showErrorMessage(e)
      } finally {
        this.loadingMore = false
      }
    },

    async saveSubmissionSession(item) {
      try {
        this.btnLoading = true
        const data = item.id
          ? await submissionSessionApi.update(item)
          : await submissionSessionApi.create(item)
        
        if (item.id) {
          this.localSubmissionSessionParent.updateSubmissionSession(data)
        } else {
          this.localSubmissionSessionParent.addSubmissionSession(data)
        }
        
        this.$toastr('success', '', 'Успешно сохранено')
        this.closeDialog()
        await this.loadInitialData()
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.btnLoading = false
      }
    },

    async removeSubmissionSession() {
      try {
        this.btnLoading = true
        await submissionSessionApi.remove(this.selectedItem.id)
        this.localSubmissionSessionParent.removeSubmissionSession(this.selectedItem)
        this.$toastr('success', '', 'Успешно удалено')
        this.closeDialog()
        await this.loadInitialData()
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.btnLoading = false
      }
    },

    edit(item) {
      this.selectedItem = item
      this.dialog = true
    },

    remove(item) {
      this.selectedItem = item
      this.confirmDialog = true
    },

    closeDialog() {
      this.dialog = false
      this.confirmDialog = false
      this.selectedItem = null
    },
  }
}
</script>